import _ from 'underscore';
import Backbone from 'backbone';
import moment from 'moment';
import Day from './Day';
import Speaker from './Speaker';
import '../backbone-relational-isomorphic';

var ensureArray = function(obj) {
    return _.isUndefined(obj) ? [] : (_.isArray(obj) ? obj : [obj]);
};

var Agenda = Backbone.RelationalModel.extend({

    relations: [
        {
            type: Backbone.HasMany,
            key: 'day',
            relatedModel: Day,
            collectionType: Backbone.Collection
        },
        {
            type: Backbone.HasMany,
            key: 'speaker',
            relatedModel: Speaker,
            collectionType: Backbone.Collection
        }
    ],

    defaults: {
        viewState: 'list'
    },

    colors: ['#acd27b', '#fa8c5c', '#2b8ec7', '#ffad65', '#5b5ea5', '#fa6a53', '#fff378', '#28b87d', '#00bcd4', '#3cafe4', '#4b76b5', '#aa64a6', '#f66da8', '#796b7d'],

    url: function() {
        return '/api/v1/event/' + this.get('eventId') + '/agenda';
    },

    parse: function(response) {
        var agenda = response.agenda ? response.agenda : {};
        agenda.day = ensureArray(agenda.day);
        agenda.speaker = ensureArray(agenda.speaker);
        _.each(agenda.speaker, function(speaker) {
            speaker.markersData = speaker.markersData || {}; // Need to have an empty object or previously existing markersData will remain in the Backbone object
        });

        this.parseAgenda(agenda);
        return agenda;
    },

    parseAgenda: function(agenda) {
        agenda.day = ensureArray(agenda.day);
        agenda.speaker = ensureArray(agenda.speaker);
        agenda['relation-session-speaker'] = ensureArray(agenda['relation-session-speaker']);
        var indexedRelations = _.groupBy(agenda['relation-session-speaker'], 'session-id');
        var indexedSpeakers = _.indexBy(agenda.speaker, 'id');

        _.each(agenda.day, function(day, dayIndex) {
            day.track = ensureArray(day.track);
            day.trackCount = day.track.length;
            day.parsedSessions = day.parsedSessions || [];
            day.parsedSessionsTracks = day.parsedSessionsTracks || [];
            var sessions = ensureArray(day.session);
            var indexedTrackRelations = _.groupBy(ensureArray(day['relation-session-track']), 'session-id');
            var indexedParsedSessionsTracks = _.indexBy(day.parsedSessionsTracks, 'trackId');

            this.calcDayStartTime(day, dayIndex);
            this.setTracksColors(day.track);

            _.each(sessions, function(session) {
                this.appendSpeakers(session, indexedSpeakers, indexedRelations);
                this.appendTracks(session, day, indexedTrackRelations, indexedParsedSessionsTracks);
                this.floorTime(session, day);
                this.calcSessionStartTime(session, day);
                session.dayIndex = dayIndex;
                session.hasSpeakers = (session.speakers.length > 0);
            }, this);

            day.hasSessions = sessions.length > 0;
        }, this);

        agenda.fetched = true;
        return agenda;
    },

    calcDayStartTime: function(day, dayIndex) {
        var startDate = this.get('startDate');
        var parsedDayDate = moment(startDate, 'YYYY-MM-DD').add(dayIndex, 'd');
        day.startDate = parsedDayDate.valueOf();
    },

    calcSessionStartTime: function(session, day) {
        var start = session.start;
        var mm = parseInt(start.substr(start.indexOf(':') + 1), 10);
        var hh = start.substr(0, start.indexOf(':'));
        session.startDate = moment(day.startDate).hours(hh).minutes(mm).valueOf();
    },

    appendSpeakers: function(session, indexedSpeakers, indexedRelations) {
        var relations = indexedRelations[session.id];
        var speakers = [];
        relations && _.each(relations, function(relation) {
            var speaker = indexedSpeakers[relation['speaker-id']];
            speaker.sessions = speaker.sessions || [];
            speaker.sessions.push(session);
            speakers.push(speaker);
        });
        session.speakers = speakers;
    },

    appendTracks: function(session, day, indexedTrackRelations, indexedParsedSessionsTracks) {
        var relations = indexedTrackRelations[session.id];
        var tracks = [];
        relations && _.each(relations, function(relation) {
            var track = _.findWhere(day.track, {'id': relation['track-id']});
            tracks.push(track);
            var parsedSessionTrack = indexedParsedSessionsTracks[track.id];
            if (parsedSessionTrack) {
                parsedSessionTrack.sessions.push(session);
            } else {
                var trackData = {
                    trackId: track.id,
                    trackName: track.name,
                    trackColor: track.color,
                    trackIndex: track.index,
                    sessions: [session]
                };
                day.parsedSessionsTracks.push(trackData);
                indexedParsedSessionsTracks[track.id] = trackData;
            }
        });
        session.tracks = tracks;

    },

    floorTime: function(session, day) {
        var divider = 30;
        day.parsedSessions = day.parsedSessions || [];
        var startTime = session.start;
        var mm = parseInt(startTime.substr(startTime.indexOf(':') + 1), 10);
        if (mm < divider) {
            mm = '00';
        } else {
            mm = '30';
        }

        session.markersData = session.markersData || {}; // Need to have an empty object or previously existing markersData will remain in the Backbone object

        var flooredTime = startTime.substr(0, startTime.indexOf(':') + 1) + mm;
        var sessionsBulk = _.findWhere(day.parsedSessions, {flooredTime: flooredTime});

        if (sessionsBulk) {
            sessionsBulk.sessions.push(session);
        } else {
            day.parsedSessions.push({
                flooredTime: flooredTime,
                sessions: [session]
            });
        }
    },

    setTracksColors: function(tracks) {
        tracks = _.isArray(tracks) ? tracks: [tracks];
        _.each(tracks, function(track, index) {
            track.color = this.colors[index % this.colors.length];
        }, this);
    },

    getDay: function(startDate, dayIndex) {
        var day;
        if (_.isNumber(dayIndex) && !_.isNaN(dayIndex)) {
            day = this.get('day').at(dayIndex);
        } else {
            var now = moment();
            var parsedEventDate = moment(startDate);
            var dayDiff = now.diff(parsedEventDate, 'day');
            if (dayDiff >= 0 && dayDiff < this.get('day').length) {
                day = this.get('day').at(dayDiff);
            } else {
                day = this.get('day').at(0);
            }
        }
        return day;
    },

    getSession: function(sessionId) {
        var session = null;
        this.get('day').each(function(day) {
            var sessions = day.get('session');
            if (sessions) {
                session = (session || sessions.get(sessionId));
            }
        });
        return session;
    }

});

export default Agenda;

